import {
  authMiddleware,
  optionalAuthMiddleware,
} from '@/helpers/authMiddleware';
import { prismaClient } from '@/helpers/prismaClient.server';
import { requireEventAccess } from '@/helpers/requireAuth.server';
import {
  extractRequestContext,
  trackEvent,
} from '@/helpers/mixpanel.server';
import { createServerFn } from '@tanstack/react-start';
import { getRequest } from '@tanstack/react-start/server';
import { z } from 'zod';
import {
  Registration,
  RegisterForEventInput,
  GetPendingApprovalsResult,
  ReviewRegistrationInput,
} from '@/apiTypes/registration';
import { isMinimumAge } from '@/utils/ageUtils';
import { createReminderSchedule } from '@/helpers/eventBridgeScheduler.server';

/**
 * Registers the authenticated user for a native event.
 * Validates that the event is native (no externalId), the category
 * belongs to the event, and the category is open for registration.
 */
export const registerForEvent = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof RegisterForEventInput> => {
      const result = RegisterForEventInput.safeParse(data);
      if (!result.success) {
        throw new Error(
          result.error.errors.map((e) => e.message).join(', '),
        );
      }
      return result.data;
    },
  )
  .handler(async ({ data, context }) => {
    const { profile } = context.auth;
    const db = await prismaClient();

    const [event, eventPrerequisites] = await Promise.all([
      db.event.findUnique({
        where: { id: data.eventId },
        select: {
          id: true,
          externalId: true,
          skipRiderFields: true,
          startDate: true,
          organizerId: true,
          requiresClubMembership: true,
          organizer: { select: { name: true } },
        },
      }),
      db.eventPrerequisite.findMany({
        where: { eventId: data.eventId },
        select: { prerequisiteEventId: true },
      }),
    ]);
    if (!event) throw new Error('Event not found');

    if (!profile.firstName || !profile.lastName) {
      throw new Error('Please complete your profile before registering');
    }

    if (!event.skipRiderFields) {
      if (!profile.liabilityWaiverAcceptedAt) {
        throw new Error('Please complete your profile before registering');
      }
    }

    if (!profile.dateOfBirth || !isMinimumAge(new Date(profile.dateOfBirth))) {
      throw new Error('You must be at least 18 years old to register');
    }

    if (event.externalId) {
      throw new Error('Cannot register natively for external events');
    }

    if (event.requiresClubMembership) {
      if (!event.organizerId) {
        throw new Error(
          'Event requires membership but has no organizer configured',
        );
      }
      const isCurrentMember = await db.organizerMember.findFirst({
        where: {
          profileId: profile.id,
          organizerId: event.organizerId,
          leftAt: null,
        },
        select: { id: true },
      });
      if (!isCurrentMember) {
        const organizerName = event.organizer?.name;
        throw new Error(
          organizerName
            ? `Membership of ${organizerName} is required to register`
            : 'Membership is required to register for this event',
        );
      }
    }

    const category = await db.category.findFirst({
      where: {
        id: data.categoryId,
        eventId: data.eventId,
      },
    });
    if (!category) throw new Error('Category not found for this event');
    if (!category.isOpen) throw new Error('Category registration is closed');
    if (category.isSoldOut) throw new Error('Category is sold out');

    if (category.routeId && data.routeId !== category.routeId) {
      throw new Error('Route does not match category');
    }

    // Gate paid categories — redirect to checkout flow
    const activePrices = await db.categoryPrice.findMany({
      where: { categoryId: data.categoryId },
      orderBy: { endsAt: { sort: 'asc', nulls: 'last' } },
    });
    const now = new Date();
    const activePrice = activePrices.find(
      (p) => p.endsAt === null || p.endsAt > now,
    );

    if (activePrice && activePrice.amount > 0) {
      const organizer = await db.organizer.findFirst({
        where: {
          events: { some: { id: data.eventId } },
          canAcceptPayments: true,
        },
        select: { id: true },
      });

      if (organizer) {
        throw new Error('PAID_EVENT_REQUIRES_CHECKOUT');
      }
    }

    const existing = await db.registration.findFirst({
      where: {
        profileId: profile.id,
        eventId: data.eventId,
        status: { in: ['APPROVED', 'PENDING_PREREQUISITE_REVIEW', 'PAID'] },
      },
    });
    if (existing) throw new Error('Already registered for this event');

    // Server-side prerequisite check — derive status rather than trusting the client.
    let registrationStatus: 'APPROVED' | 'PENDING_PREREQUISITE_REVIEW' =
      'APPROVED';
    let offPlatformClaim = false;
    let claimedAt: Date | undefined;

    if (eventPrerequisites.length > 0) {
      const prerequisiteIds = eventPrerequisites.map(
        (p) => p.prerequisiteEventId,
      );
      const satisfiedRegistrations = await db.registration.findMany({
        where: { profileId: profile.id, eventId: { in: prerequisiteIds } },
        select: { eventId: true },
      });
      const satisfiedIds = new Set(
        satisfiedRegistrations.map((r) => r.eventId),
      );
      const allSatisfied = prerequisiteIds.every((id) =>
        satisfiedIds.has(id),
      );

      if (!allSatisfied) {
        if (!data.offPlatformClaim) {
          throw new Error(
            'You must complete all prerequisites before registering',
          );
        }
        registrationStatus = 'PENDING_PREREQUISITE_REVIEW';
        offPlatformClaim = true;
        claimedAt = new Date();
      }
    }

    const registration = await db.registration.create({
      data: {
        profileId: profile.id,
        eventId: data.eventId,
        routeId: data.routeId,
        categoryId: data.categoryId,
        status: registrationStatus,
        offPlatformClaim,
        ...(claimedAt && { claimedAt }),
      },
    });

    // Schedule reminder email 24h before event start
    const reminderBaseTime = category.startTime ?? event.startDate;
    if (reminderBaseTime) {
      const fireAt = new Date(reminderBaseTime.getTime() - 24 * 60 * 60 * 1000);
      createReminderSchedule({
        profileId: profile.id,
        eventId: data.eventId,
        type: 'REGISTRATION',
        fireAt,
      }).catch((err) =>
        console.error('Failed to schedule registration reminder:', err),
      );
    }

    const request = getRequest();
    const requestContext = request ? extractRequestContext(request) : undefined;
    await trackEvent(
      profile.id,
      'Event Registered',
      {
        event_id: data.eventId,
        intent: 'register',
        category_id: data.categoryId,
        route_id: data.routeId,
        registration_status: registrationStatus,
      },
      requestContext,
    );

    return { registration: Registration.parse(registration) };
  });

/**
 * Checks whether the current user is registered for a given event.
 * Returns isRegistered: false if not authenticated (uses optionalAuth).
 */
export const getRegistrationStatus = createServerFn({ method: 'GET' })
  .middleware([optionalAuthMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid event ID format');
    return result.data;
  })
  .handler(async ({ data: eventId, context }) => {
    const auth = context.auth;
    if (!auth) return { isRegistered: false as const, registration: null };

    const db = await prismaClient();
    const registration = await db.registration.findFirst({
      where: {
        profileId: auth.profile.id,
        eventId,
        status: { in: ['APPROVED', 'PENDING_PREREQUISITE_REVIEW', 'PAID'] },
      },
      orderBy: { createdAt: 'desc' },
    });

    if (!registration) {
      return { isRegistered: false as const, registration: null };
    }

    return {
      isRegistered: true as const,
      registration: Registration.parse(registration),
    };
  });

/**
 * Returns events from the current month onward that the authenticated user has registered for,
 * ordered by startDate ascending. Returns [] when unauthenticated.
 */
export const getProfileRegisteredEvents = createServerFn({ method: 'GET' })
  .middleware([optionalAuthMiddleware])
  .handler(async ({ context }) => {
    const auth = context.auth;
    if (!auth) return [];

    const db = await prismaClient();
    const startOfMonth = new Date();
    startOfMonth.setDate(1);
    startOfMonth.setHours(0, 0, 0, 0);
    const registrations = await db.registration.findMany({
      where: {
        profileId: auth.profile.id,
        // Matches the event page's own-registration check; excludes HOLD/EXPIRED/DENIED
        status: { in: ['APPROVED', 'PENDING_PREREQUISITE_REVIEW', 'PAID'] },
        event: { archived: false },
        OR: [
          { event: { endDate: { gte: startOfMonth } } },
          { event: { endDate: null, startDate: { gte: startOfMonth } } },
        ],
      },
      orderBy: { event: { startDate: 'asc' } },
      select: {
        event: {
          select: {
            id: true,
            name: true,
            startDate: true,
            endDate: true,
            location: true,
            heroUrl: true,
            eventType: true,
            discipline: true,
            externalId: true,
            lat: true,
            lng: true,
          },
        },
      },
    });

    return registrations.map((r) => r.event);
  });

const PARTICIPANT_PREVIEW_LIMIT = 8;

/**
 * Fetches registered participants for an event, returning a preview
 * list (up to 8) with profile info and a total count.
 */
export const getEventParticipants = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid event ID format');
    return result.data;
  })
  .handler(async ({ data: eventId }) => {
    const db = await prismaClient();

    const [registrations, totalCount] = await Promise.all([
      db.registration.findMany({
        where: { eventId, status: {in: ['APPROVED', 'PAID']} },
        take: PARTICIPANT_PREVIEW_LIMIT,
        orderBy: { createdAt: 'desc' },
        select: {
          id: true,
          createdAt: true,
          profile: {
            select: {
              id: true,
              firstName: true,
              lastName: true,
              displayName: true,
              avatarUrl: true,
            },
          },
        },
      }),
      db.registration.count({ where: { eventId, status: {in: ['APPROVED', 'PAID']} } }),
    ]);

    return {
      participants: registrations.map((r) => ({
        id: r.id,
        registeredAt: r.createdAt,
        profile: r.profile,
      })),
      totalCount,
    };
  });

/**
 * Fetches every registered participant for an event with profile info,
 * ordered most-recent first. Used by the public "View All Participants"
 * modal on the event page.
 */
export const getAllEventParticipants = createServerFn({method: 'GET'})
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid event ID format');
    return result.data;
  })
  .handler(async ({data: eventId}) => {
    const db = await prismaClient();

    const registrations = await db.registration.findMany({
      where: {eventId, status: {in: ['APPROVED', 'PAID']}},
      orderBy: {createdAt: 'desc'},
      select: {
        id: true,
        createdAt: true,
        profile: {
          select: {
            id: true,
            firstName: true,
            lastName: true,
            displayName: true,
            avatarUrl: true,
          },
        },
      },
    });

    return registrations.map((r) => ({
      id: r.id,
      registeredAt: r.createdAt,
      profile: r.profile,
    }));
  });

const GetDashboardRegistrationsInput = z.object({
  eventId: z.string().uuid(),
  page: z.number().int().positive().default(1),
  pageSize: z.number().int().positive().max(100).default(25),
});

/**
 * Fetches paginated registrations for the event dashboard.
 * Requires organizer-level access to the event. Returns profile,
 * category, and route info for each registration.
 */
export const getDashboardRegistrations = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator(
    (
      data: unknown,
    ): z.infer<typeof GetDashboardRegistrationsInput> => {
      const result = GetDashboardRegistrationsInput.safeParse(data);
      if (!result.success) {
        throw new Error(
          result.error.errors.map((e) => e.message).join(', '),
        );
      }
      return result.data;
    },
  )
  .handler(async ({ data, context }) => {
    await requireEventAccess(data.eventId, context.auth);
    const db = await prismaClient();

    const skip = (data.page - 1) * data.pageSize;

    const [registrations, totalCount] = await Promise.all([
      db.registration.findMany({
        where: { eventId: data.eventId, status: {in: ['APPROVED', 'PAID']} },
        skip,
        take: data.pageSize,
        orderBy: { createdAt: 'desc' },
        select: {
          id: true,
          createdAt: true,
          profile: {
            select: {
              id: true,
              email: true,
              firstName: true,
              lastName: true,
              displayName: true,
              avatarUrl: true,
            },
          },
          category: {
            select: { id: true, name: true },
          },
          route: {
            select: { id: true, name: true },
          },
        },
      }),
      db.registration.count({ where: { eventId: data.eventId, status: {in: ['APPROVED', 'PAID']} } }),
    ]);

    return {
      registrations,
      totalCount,
      page: data.page,
      pageSize: data.pageSize,
      totalPages: Math.ceil(totalCount / data.pageSize),
    };
  });

/**
 * Returns registrations for an event that are pending organizer review
 * or have been denied, grouped into `pending` and `denied` lists.
 * Requires organizer-level access to the event.
 */
export const getPendingApprovals = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) throw new Error('Invalid event ID format');
    return result.data;
  })
  .handler(async ({ data: eventId, context }) => {
    await requireEventAccess(eventId, context.auth);
    const db = await prismaClient();

    const [registrations, eventPrerequisites] = await Promise.all([
      db.registration.findMany({
        where: {
          eventId,
          status: { in: ['PENDING_PREREQUISITE_REVIEW', 'DENIED'] },
          claimedAt: { not: null },
        },
        select: {
          id: true,
          claimedAt: true,
          status: true,
          profile: {
            select: {
              id: true,
              email: true,
              firstName: true,
              lastName: true,
              displayName: true,
              avatarUrl: true,
            },
          },
        },
        orderBy: { claimedAt: 'asc' },
      }),
      db.eventPrerequisite.findMany({
        where: { eventId },
        select: {
          prerequisiteEvent: { select: { id: true, name: true } },
        },
      }),
    ]);

    const prerequisites = eventPrerequisites.map((p) => p.prerequisiteEvent);
    const items = registrations.map((r) => ({ ...r, prerequisites }));

    return GetPendingApprovalsResult.parse({
      pending: items.filter(
        (r) => r.status === 'PENDING_PREREQUISITE_REVIEW',
      ),
      denied: items.filter((r) => r.status === 'DENIED'),
    });
  });

/**
 * Approves a registration that is pending prerequisite review.
 * Sets status to APPROVED, records the review timestamp and the
 * admin row of the reviewer (null for superadmins).
 */
export const approveRegistration = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator((data: unknown): ReviewRegistrationInput => {
    const result = ReviewRegistrationInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({ data, context }) => {
    const auth = await requireEventAccess(data.eventId, context.auth);
    const db = await prismaClient();

    const membership = await db.organizerAdmin.findFirst({
      where: {
        profileId: auth.profile.id,
        organizer: { events: { some: { id: data.eventId } } },
      },
      select: { organizerId: true },
    });

    const updated = await db.registration.updateMany({
      where: {
        id: data.registrationId,
        eventId: data.eventId,
        status: { in: ['PENDING_PREREQUISITE_REVIEW', 'DENIED'] },
      },
      data: {
        status: 'APPROVED',
        reviewedAt: new Date(),
        reviewedByOrganizerId: membership?.organizerId ?? null,
      },
    });

    if (updated.count === 0) {
      throw new Error('Registration not found or not eligible for approval');
    }
  });

/**
 * Denies a registration that is pending prerequisite review.
 * Sets status to DENIED, records the review timestamp and the
 * admin row of the reviewer (null for superadmins).
 */
export const denyRegistration = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator((data: unknown): ReviewRegistrationInput => {
    const result = ReviewRegistrationInput.safeParse(data);
    if (!result.success) {
      throw new Error(result.error.errors.map((e) => e.message).join(', '));
    }
    return result.data;
  })
  .handler(async ({ data, context }) => {
    const auth = await requireEventAccess(data.eventId, context.auth);
    const db = await prismaClient();

    const membership = await db.organizerAdmin.findFirst({
      where: {
        profileId: auth.profile.id,
        organizer: { events: { some: { id: data.eventId } } },
      },
      select: { organizerId: true },
    });

    const updated = await db.registration.updateMany({
      where: {
        id: data.registrationId,
        eventId: data.eventId,
        status: 'PENDING_PREREQUISITE_REVIEW',
      },
      data: {
        status: 'DENIED',
        reviewedAt: new Date(),
        reviewedByOrganizerId: membership?.organizerId ?? null,
      },
    });

    if (updated.count === 0) {
      throw new Error('Registration not found or not eligible for denial');
    }
  });
